import { DIFFICULTY_CONFIG } from '../../../config/difficulty.config';
import { WORLD_CONFIG } from '../../../config/world.config';
import { DifficultyManager } from '../../../difficulty/DifficultyManager';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { DEG_TO_RAD } from '../../../utils/math';
import { deckLandingZ, deckLineHeight, type DeckProfile } from '../deckLines';
import {
  COLLECTIBLE_FLOAT_HEIGHT,
  collectibleLine,
  entryConnector,
  laneCenterX,
  moduleSides,
  obstacleRowOn,
  standardLanes,
  straightExit,
  type CollectibleRun,
} from '../segmentBuilders';
import {
  SegmentKind,
  type CollectibleSpawnPoint,
  type FixedObstaclePlacement,
  type GroundSurface,
  type SegmentDefinition,
  type SegmentGenerationRules,
  type SegmentVisualPart,
} from '../SegmentDefinition';

/**
 * SEG_CONTAINERS_40M – "Containerdach": a row of 20 ft shipping containers stands in the left lane.
 * A double jump gets the runner onto the roofs (user 2026-10-02: "wenn wir einen Doppelsprung machen,
 * dass man darauf kommt, auf den Container") – bottles up there, a drop back to the road at the end.
 * Lanes 1 and 2 stay the ground route with jump and slide obstacles.
 *
 *   Container   ENV_CONTAINER_A, 6.06 × 2.59 × 2.44 m (ISO 668 1CC), long side along the track, doors
 *               facing the runner, ructec badge to the track. 2.44 m is wider than a lane (2.2 m): the
 *               outer face stands 5 cm inside the track edge (the red/white barriers start 3 cm outside
 *               it), the inner face reaches 0.29 m into the middle lane – x −3.25 … −0.81.
 *   Row         z 14 – 26.42: two containers, 0.3 m apart (the runner, 0.6 m deep, runs across).
 *   Face        a wall (frontal hit). A double jump gets up: rise 2.59 m (step limit 2.7 m). JumpReach
 *               (1/120 s steps like the game): 0.35 s input window for the second jump; meeting the face
 *               0.46–1.03 s after take-off leaves ≥ 0.2 s of it. The 14 m run-up keeps 0.30 s of take-off
 *               timing at 18 m/s (0.57 s at 8–10 m/s) – terrainAnalysis proves it, the validator keeps lane 0
 *               free before the face, tests/unit/containers.test.ts replays it with the PlayerController.
 *   Drop        z 26.42: 2.59 m fall, lands ≤ 8.9 m later at 18 m/s (z 35.3) – lane 0 stays free to the end
 *               of the module (the next module's rows are ≥ 5.4 m further: one reaction time). Sideways
 *               off the roof is a drop as well (like the ramp platform).
 *   Ground      R06 / R20 / R26 / R32 in lanes 1–2, one or two obstacles a row. Lane 1 next to the
 *               containers (R20, R26) takes narrow obstacles only (≤ 1.3 m, free width 1.62 m). Walls
 *               between the lanes along the row: jumps, double jumps and slides only.
 *   Bottles     on the roof (from lane 0, or from the middle lane switching early) or along lane 2.
 *   Length      40 m: a 60 m open straight would push the ad gantry past its target spacing.
 *
 * Collider = terrain: one surface per container, exactly the container box.
 */
export const CONTAINER_20FT = {
  assetId: 'ENV_CONTAINER_A',
  /** Body of ENV_CONTAINER_A (tools/blender/assets/ENV_Container_A.py, greybox fallback): long side, height, width. */
  length: 6.06,
  height: 2.59,
  width: 2.44,
  /** +90° lays the asset's long side (local x) along the track: doors (local +x) face the runner, badge side (local +z) to the right. */
  yawDoorsToRunner: 90 * DEG_TO_RAD,
} as const;

export const CONTAINERS = {
  length: 40,
  lane: 0,
  /** Face of the first container – the run-up before it is the module's own flat ground. */
  zFrom: 14,
  count: 2,
  /** Gap between the containers – door hardware stands 4 cm proud, the runner (0.6 m deep) runs across. */
  gap: 0.3,
  /** Outer container face this far inside the track edge (the red/white barriers start 3 cm outside it). */
  edgeClearance: 0.05,
  yaw: CONTAINER_20FT.yawDoorsToRunner,
  /** Obstacle rows (z) – 6 m apart: one reaction time at top speed. */
  rows: { R06: 6, R20: 20, R26: 26, R32: 32 },
  collectibleSpacing: 2,
  /** The middle-lane bottle line moves over to the roof lane here (before the take-off). */
  switchToRoofZ: 5,
} as const;

/** One container: box on the floor plan (= terrain surface) and its visual placement. */
export interface ContainerBox {
  readonly id: string;
  readonly lane: number;
  readonly zFrom: number;
  readonly zTo: number;
  readonly xFrom: number;
  readonly xTo: number;
  readonly height: number;
  readonly yaw: number;
}

/** The containers end to end with `CONTAINERS.gap`, outer face just inside the track edge. */
function containerRow(): ContainerBox[] {
  const { length, width, height } = CONTAINER_20FT;
  const edge = WORLD_CONFIG.trackWidth / 2 - CONTAINERS.edgeClearance;
  const outside = Math.sign(laneCenterX(WORLD_CONFIG, CONTAINERS.lane)) < 0;
  const xFrom = outside ? -edge : edge - width;
  return Array.from({ length: CONTAINERS.count }, (_, i) => {
    const zFrom = CONTAINERS.zFrom + i * (length + CONTAINERS.gap);
    return { id: `${i + 1}`, lane: CONTAINERS.lane, zFrom, zTo: zFrom + length, xFrom, xTo: xFrom + width, height, yaw: CONTAINERS.yaw };
  });
}

export const CONTAINER_BOXES: readonly ContainerBox[] = containerRow();
/** Start and end (z) of the row. */
export const CONTAINER_ROW = { zFrom: CONTAINER_BOXES[0]?.zFrom ?? 0, zTo: CONTAINER_BOXES[CONTAINER_BOXES.length - 1]?.zTo ?? 0 } as const;

const surfaces: GroundSurface[] = CONTAINER_BOXES.map((box) => ({
  id: `SURF_CONTAINER_${box.id}`,
  zFrom: box.zFrom,
  zTo: box.zTo,
  xFrom: box.xFrom,
  xTo: box.xTo,
  heightFrom: box.height,
  heightTo: box.height,
}));

const containerVisuals: SegmentVisualPart[] = CONTAINER_BOXES.map((box) => ({
  assetId: CONTAINER_20FT.assetId,
  // asset origin = ground centre of the body
  position: { x: (box.xFrom + box.xTo) / 2, y: WORLD_CONFIG.groundHeight, z: (box.zFrom + box.zTo) / 2 },
  yaw: box.yaw,
}));

const GENERATION: SegmentGenerationRules = {
  // A highlight once double jumps are introduced (tier T1 at 500 m): ≈ 0.75 per km, the first one after
  // ≈ 1 km (median), never closer than 500 m – measured in tests/unit/containers.test.ts.
  weight: 0.3,
  minDistance: 300,
  cooldownDistance: 500,
  minSegmentsAfterJunction: 1,
  family: 'CONTAINERS',
  tags: ['containers', 'terrain', 'platform', 'double-jump'],
};

// ── Obstacles ────────────────────────────────────────────────────────────────

const [L0, L1, L2] = [CONTAINERS.lane, 1, WORLD_CONFIG.laneCount - 1] as const;
/** Away from the containers: jumps and slides – the walls along the row forbid lane changes. */
const OPEN_TYPES = [ObstacleType.Jump, ObstacleType.Slide] as const;
/** Lane 1 next to a container: narrow jump / double-jump obstacles only (slide frames span the whole lane). */
const NARROW_TYPES = [ObstacleType.Jump, ObstacleType.DoubleJump] as const;
/** Obstacles narrow enough for lane 1 next to a container (visual ≤ 1.3 m; the free width there is 1.62 m). */
export const NARROW_OBSTACLES: readonly string[] = ['OBS_PALLET_A', 'OBS_BIG_BAG_A', 'OBS_CABLE_DRUM_A', 'OBS_BRICK_STACK_A'];

const { R06, R20, R26, R32 } = CONTAINERS.rows;
const spawnPoints = [
  ...obstacleRowOn(WORLD_CONFIG, 'R06', R06, [L1, L2], OPEN_TYPES),
  ...obstacleRowOn(WORLD_CONFIG, 'R20', R20, [L1], NARROW_TYPES),
  ...obstacleRowOn(WORLD_CONFIG, 'R20', R20, [L2], OPEN_TYPES),
  ...obstacleRowOn(WORLD_CONFIG, 'R26', R26, [L1], NARROW_TYPES),
  ...obstacleRowOn(WORLD_CONFIG, 'R26', R26, [L2], OPEN_TYPES),
  ...obstacleRowOn(WORLD_CONFIG, 'R32', R32, [L1, L2], OPEN_TYPES),
];

const at = (row: string, lane: number, obstacleId: string): FixedObstaclePlacement => ({ spawnPointId: `SOCKET_OBS_${row}_L${lane}`, obstacleId });

// ── Bottles ──────────────────────────────────────────────────────────────────

/** Bottle lines show the ideal path at the speed the module is first met. */
const LINE_SPEED = new DifficultyManager(DIFFICULTY_CONFIG).speedAt(GENERATION.minDistance);
/** The roof row as the bottle line sees it: up with a double jump, along the roofs, the drop after the end (deckLines.ts). */
const ROOF_DECK: DeckProfile = { face: CONTAINER_ROW.zFrom, end: CONTAINER_ROW.zTo, top: CONTAINER_20FT.height };

const SPACING = CONTAINERS.collectibleSpacing;
/** The roof line ends one bottle after the drop has reached the ground. */
const ROOF_LINE_END = Math.ceil(deckLandingZ(ROOF_DECK, LINE_SPEED)) + SPACING;
const roofRun: CollectibleRun = { lane: L0, from: 1, to: ROOF_LINE_END, spacing: SPACING, height: deckLineHeight(ROOF_DECK, LINE_SPEED) };
const flat = () => COLLECTIBLE_FLOAT_HEIGHT;

/** From the middle lane over to the roof lane before the take-off – own socket ids (lane 0 is shared with the roof line). */
function middleToRoof(): CollectibleSpawnPoint[] {
  return collectibleLine(WORLD_CONFIG, 'MIDDLE_TO_ROOF', [
    { lane: L1, from: 1, to: CONTAINERS.switchToRoofZ - SPACING, spacing: SPACING, height: flat },
    { ...roofRun, from: CONTAINERS.switchToRoofZ },
  ]).map((point) => ({ ...point, id: point.id.replace('SOCKET_COL_', 'SOCKET_COL_M_') }));
}

const collectibles: CollectibleSpawnPoint[] = [
  ...collectibleLine(WORLD_CONFIG, 'ROOF', [roofRun]),
  ...middleToRoof(),
  ...collectibleLine(WORLD_CONFIG, 'GROUND_L2', [{ lane: L2, from: 1, to: CONTAINERS.length - 1, spacing: SPACING, height: flat }]),
];

export const SEG_CONTAINERS_40M: SegmentDefinition = {
  id: 'SEG_CONTAINERS_40M',
  kind: SegmentKind.Straight,
  displayName: 'Containerdach 40 m',
  length: CONTAINERS.length,
  width: WORLD_CONFIG.trackWidth,
  // Two base road modules; the containers are the realistic ENV_CONTAINER_A (its greybox box is the fallback).
  visualAssetId: 'SEG_STRAIGHT_20M',
  extraVisuals: [{ assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } }, ...containerVisuals],

  entry: entryConnector(),
  exits: [straightExit(CONTAINERS.length)],
  lanes: standardLanes(WORLD_CONFIG),

  terrain: { surfaces, gaps: [], ceilings: [] },

  obstacleSpawnPoints: spawnPoints,
  obstacleRowLayouts: { sparse: [], dense: [] },
  obstacleVariants: [
    {
      id: 'GROUND_RIGHT_FIRST',
      weight: 1,
      placements: [at('R06', L2, 'OBS_BARRIER_A'), at('R20', L1, 'OBS_CABLE_DRUM_A'), at('R26', L2, 'OBS_SCAFFOLD_PASS_A'), at('R32', L1, 'OBS_CONE_ROW_A')],
    },
    {
      id: 'GROUND_MIDDLE_FIRST',
      weight: 1,
      placements: [at('R06', L1, 'OBS_PALLET_A'), at('R20', L2, 'OBS_SCAFFOLD_PASS_A'), at('R26', L1, 'OBS_BRICK_STACK_A'), at('R32', L2, 'OBS_BARRIER_BOARD_A')],
    },
    {
      id: 'GROUND_BOTH',
      weight: 1,
      placements: [
        at('R06', L1, 'OBS_CONE_ROW_A'),
        at('R06', L2, 'OBS_SCAFFOLD_PASS_A'),
        at('R20', L1, 'OBS_BIG_BAG_A'),
        at('R20', L2, 'OBS_BARRIER_A'),
        at('R26', L1, 'OBS_PALLET_A'),
        at('R26', L2, 'OBS_SCAFFOLD_PASS_A'),
        at('R32', L1, 'OBS_BARRIER_BOARD_A'),
        at('R32', L2, 'OBS_PALLET_A'),
      ],
    },
  ],

  collectibleSpawnPoints: collectibles,

  ...moduleSides(CONTAINERS.length),

  generation: GENERATION,
};
